/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import DatePicker, { registerLocale } from "react-datepicker";
import { ptBR, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

import "react-datepicker/dist/react-datepicker.css";
import { getConditionBgClass, periodList } from "../Utils";
import { CustomDatepickerInput } from "../components/CustomDatepickerInput";
import { AnomalyRecord } from "data/entities/AnomalyRecord";
import { ConditionType, ConditionTypeLabel } from "../model/enumerations/condition-type.model";
import dayjs from "dayjs";
import { getDateLocaleConfig } from "i18n/locale";

registerLocale("pt-BR", ptBR);
registerLocale("en-US", enUS);

type SelectChange = React.ChangeEvent<HTMLSelectElement>;
type InputChange = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

interface ThermalAnalysisDataInfoProps {
    mode: string | null;
    anomalyRecord: AnomalyRecord;

    equipmentComponentLimites: any[];
    riskPeriodDeadlineList: any[];

    onInputChange?: (e: InputChange) => void;
    onSelectChange?: (e: SelectChange) => void;
    onComponentChange?: (e: SelectChange) => void;
    onDeltaTChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onConditionChange?: (e: SelectChange) => void;

    handleDeadlineExecutionChange?: (date: Date | null) => void;
}

const valueTextStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    whiteSpace: "pre-wrap",
};

const conditionBodyTemplate = (condition: any) => {
    if (condition === "NORMAL" || !condition) {
        return <div>{ConditionTypeLabel[condition] ?? ""}</div>;
    }

    const bgClass = getConditionBgClass(condition);
    return <div className={`cell-content ${bgClass}`}>{ConditionTypeLabel[condition]}</div>;
};

const ThermalAnalysisDataInfo = ({
    mode,
    anomalyRecord,
    equipmentComponentLimites,
    riskPeriodDeadlineList,
    onInputChange,
    onSelectChange,
    onComponentChange,
    onDeltaTChange,
    onConditionChange,
    handleDeadlineExecutionChange,
}: ThermalAnalysisDataInfoProps) => {
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const dateLocale = getDateLocaleConfig(i18n.language);
    // VIEW: controla apenas DISABLED
    const isView = mode === "view";

    // REPORT: controla o modo de visualização (ValueText / layout)
    const isReport = mode === "report";

    const imminent = anomalyRecord.condition === "IMMINENT_HIGH_RISK";

    const ValueText = ({ children }: { children: React.ReactNode }) => (
        <span className="thermo-report__value" style={valueTextStyle}>
            {children ?? "--"}
        </span>
    );

    const getEquipmentComponentLabel = () => {
        const found = equipmentComponentLimites?.find(
            (x) => String(x.id) === String(anomalyRecord.component?.id)
        );
        return found?.name ?? "--";
    };

    const getPeriodicityLabel = () => {
        // const found = periodList?.find((x) => String(x.id) === String(anomalyRecord.nextMonitoring));
        // return found?.name ?? tt("pages.thermography.thermalAnalysisDataInfo.noMonitoring");
        if (!anomalyRecord.nextMonitoring) return "--";
        const d = dayjs(anomalyRecord.nextMonitoring);
        return d.isValid() ? d.format("DD-MM-YYYY") : "--";
    };

    const getDeadlineExecutionLabel = () => {
        if (!anomalyRecord.deadlineExecution) return "--";
        const d = dayjs(anomalyRecord.deadlineExecution);
        return d.isValid() ? d.format("DD-MM-YYYY") : "--";
    };

    return (
        <Form.Group className="bg-body px-2 thermal-analysis-info" controlId="">
            {/* LINHA 1: Equip/Comp + DeltaT */}
            <Row className="mt-3 tai-desc">
                <Col xl={8}>
                    <Form.Label>{tt("pages.thermography.thermalAnalysisDataInfo.equipmentComponent")}</Form.Label>

                    {isReport ? (
                        <ValueText>{getEquipmentComponentLabel()}</ValueText>
                    ) : (
                        <Form.Select
                            name="component"
                            value={anomalyRecord.component?.id || ""}
                            onChange={onComponentChange}
                            required
                            disabled={anomalyRecord.equipment?.id === "" || isView}
                        >
                            <option value="">{tt("pages.thermography.thermalAnalysisDataInfo.selectEquipmentComponent")}</option>
                            {equipmentComponentLimites.map((item) => (
                                <option key={item.id} value={item.id} data-type="text">
                                    {item.name}
                                </option>
                            ))}
                        </Form.Select>
                    )}
                </Col>

                <Col xl={4}>
                    <Form.Label>{tt("pages.thermography.thermalAnalysisDataInfo.deltaT")}</Form.Label>

                    {isReport ? (
                        <ValueText>
                            {anomalyRecord.deltaT != null ? Number(anomalyRecord.deltaT).toFixed(1) : "--"}
                        </ValueText>
                    ) : (
                        <Form.Control
                            type="number"
                            name="deltaT"
                            value={anomalyRecord.deltaT?.toFixed(1)}
                            onChange={onDeltaTChange}
                            required
                            disabled={anomalyRecord.equipment?.id === "" || isView}
                            className={imminent ? "imminent-high-risk" : ""}
                        />
                    )}
                </Col>
            </Row>

            <Row className="mt-3 tai-line2">
                <Col xl={isReport ? 0 : 4}>
                    <Form.Label>{tt("pages.thermography.thermalAnalysisDataInfo.condition")}</Form.Label>

                    {isReport ? (
                        <ValueText>{conditionBodyTemplate(anomalyRecord.condition)}</ValueText>
                    ) : (
                        <Form.Select
                            name="condition"
                            value={anomalyRecord.condition}
                            onChange={onConditionChange}
                            required
                            disabled={
                                anomalyRecord.equipment?.id === "" ||
                                (anomalyRecord.component?.id != undefined && anomalyRecord.component?.id == "") ||
                                isView
                            }
                            data-type="text"
                            className={imminent ? "imminent-high-risk" : ""}
                        >
                            <option value="">{tt("pages.thermography.thermalAnalysisDataInfo.selectCondition")}</option>
                            {Object.values(ConditionType).map((type) => (
                                <option key={type} value={type}>
                                    {ConditionTypeLabel[type]}
                                </option>
                            ))}
                        </Form.Select>
                    )}
                </Col>

                <Col xl={4}>
                    <Form.Label>{tt("pages.thermography.thermalAnalysisDataInfo.monitoring")}</Form.Label>

                    {isReport ? (
                        <ValueText>{getPeriodicityLabel()}</ValueText>
                    ) : (
                        <Form.Select
                            name="periodicity"
                            value={anomalyRecord.periodicity || ""}
                            onChange={onSelectChange}
                            disabled
                        >
                            <option value="">{tt("pages.thermography.thermalAnalysisDataInfo.noMonitoring")}</option>
                            {periodList.map((period) => (
                                <option key={period.id} value={period.id}>
                                    {period.name}
                                </option>
                            ))}
                        </Form.Select>
                    )}
                </Col>

                <Col xl={4}>
                    <Form.Label>{tt("pages.thermography.thermalAnalysisDataInfo.executionDeadline")}</Form.Label>

                    {isReport ? (
                        <ValueText>{getDeadlineExecutionLabel()}</ValueText>
                    ) : (
                        <Form.Group>
                            <DatePicker
                                key={`analysis-deadline-${i18n.language}`}
                                locale={dateLocale.datePickerLocale}
                                selected={
                                    anomalyRecord?.deadlineExecution
                                        ? dayjs(anomalyRecord?.deadlineExecution).toDate()
                                        : null
                                }
                                onChange={handleDeadlineExecutionChange}
                                dateFormat={dateLocale.datePickerFormat}
                                customInput={
                                    <CustomDatepickerInput
                                        placeholder={dateLocale.datePickerPlaceholder}
                                        className={imminent ? "imminent-high-risk" : ""}
                                        disabled
                                    />
                                }
                                disabled
                            />
                        </Form.Group>
                    )}
                </Col>
            </Row>

            {/* LINHA: Descrição da análise */}
            <Row className="mt-3 tai-line1">
                <Col xl={12}>
                    <Form.Label>{tt("pages.thermography.thermalAnalysisDataInfo.analysisDescription")}</Form.Label>

                    {isReport ? (
                        <ValueText>{anomalyRecord.analysisDescription || "--"}</ValueText>
                    ) : (
                        <Form.Control
                            as="textarea"
                            rows={4}
                            name="analysisDescription"
                            value={anomalyRecord.analysisDescription}
                            disabled={isView}
                            onChange={onInputChange}
                        />
                    )}
                </Col>
            </Row>

            {/* LINHA: Recomendações */}
            <Row className="mt-3 tai-reco">
                <Col xl={12}>
                    <Form.Label className={isReport ? "" : "mt-3"}>
                        {tt("pages.thermography.thermalAnalysisDataInfo.recommendations")}
                    </Form.Label>

                    {isReport ? (
                        <ValueText>{anomalyRecord.recommendations || "--"}</ValueText>
                    ) : (
                        <Form.Control
                            as="textarea"
                            rows={6}
                            name="recommendations"
                            value={anomalyRecord.recommendations || ""}
                            onChange={onInputChange}
                            disabled={anomalyRecord.equipment?.id === "" || isView}
                        />
                    )}
                </Col>
            </Row>
        </Form.Group>
    );
};

export default ThermalAnalysisDataInfo;
